import React, { useEffect, useRef, useState } from "react"
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Button,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Progress,
  Text,
  VStack,
} from "@chakra-ui/react"
import { WarningTwoIcon } from "@chakra-ui/icons"
import { useNavigate } from "react-router-dom"
import { Api } from "../lib/api"
import { addAlert, AlertType } from "../lib/alerts"
import { Datasets, DatasetSchemas, IsDeleting, Schemas } from "../lib/store"

const COUNTDOWN_SECONDS = 3
const POLL_INTERVAL_MS = 2000

/**
 * Three-step destructive delete confirmation modal.
 *
 * Props:
 *   isOpen        - boolean
 *   onClose       - called after successful delete or on cancel
 *   type          - "dataset" | "schema" | "records"
 *   name          - display name of the thing being deleted (datasets/schemas)
 *   id            - the id of the dataset or schema being deleted
 *   recordCount   - number of records that will be destroyed
 *   selectedIds   - array of record IDs (type="records" only)
 *   navigateTo    - path to redirect to after successful delete (optional)
 */
export default function DeleteConfirmModal({
  isOpen,
  onClose,
  type,
  name,
  id,
  recordCount = 0,
  selectedIds = [],
  navigateTo,
}) {
  const [step, setStep] = useState("confirm") // "confirm" | "verify" | "deleting"
  const [inputValue, setInputValue] = useState("")
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS)
  const [progress, setProgress] = useState(null) // null | {processed, total, status}
  const [error, setError] = useState(null)
  const countdownRef = useRef(null)
  const pollRef = useRef(null)
  const navigate = useNavigate()

  const api = new Api()

  const count = type === "records" ? selectedIds.length : recordCount
  const expectedPhrase = type === "records" ? `DELETE ${count} RECORDS` : name

  const confirmMatches = inputValue === expectedPhrase

  const descriptionText = () => {
    if (type === "dataset") {
      return `This will permanently delete the dataset "${name}" and all of its schemas and encrypted records. There is no undo.`
    }
    if (type === "schema") {
      return count > 0
        ? `This will permanently delete the schema "${name}" and all ${count.toLocaleString()} encrypted records. There is no undo.`
        : `This will permanently delete the schema "${name}". There is no undo.`
    }
    return `This will permanently delete ${count.toLocaleString()} selected record${
      count === 1 ? "" : "s"
    }. There is no undo.`
  }

  const confirmPrompt = () => {
    if (type === "records") return `Type DELETE ${count} RECORDS to continue:`
    return `Type the ${type} name to continue:`
  }

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setStep("confirm")
      setInputValue("")
      setCountdown(COUNTDOWN_SECONDS)
      setProgress(null)
      setError(null)
    } else {
      clearInterval(countdownRef.current)
      clearInterval(pollRef.current)
    }
  }, [isOpen])

  // Countdown timer for step 2
  useEffect(() => {
    if (step === "verify") {
      setCountdown(COUNTDOWN_SECONDS)
      countdownRef.current = setInterval(() => {
        setCountdown((c) => {
          if (c <= 1) {
            clearInterval(countdownRef.current)
            return 0
          }
          return c - 1
        })
      }, 1000)
    }
    return () => clearInterval(countdownRef.current)
  }, [step])

  const handleConfirmNext = () => {
    setStep("verify")
  }

  const handleBack = () => {
    setStep("confirm")
    setInputValue("")
    clearInterval(countdownRef.current)
  }

  const removeFromStore = () => {
    if (type === "dataset") {
      Datasets.updateValue((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
      DatasetSchemas.updateValue((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    } else if (type === "schema") {
      Schemas.updateValue((prev) => {
        const next = { ...prev }
        delete next[id]
        return next
      })
    }
  }

  const finishDelete = () => {
    IsDeleting.setValue(false)
    removeFromStore()
    addAlert(
      AlertType.SUCCESS,
      "Delete",
      `${
        type === "records" ? `${count} records` : `"${name}"`
      } deleted successfully.`,
    )
    clearInterval(pollRef.current)
    onClose()
    if (navigateTo) navigate(navigateTo)
  }

  const startPolling = (jobId) => {
    pollRef.current = setInterval(async () => {
      try {
        const status = await api.jobsDeleteStatus(jobId)
        setProgress(status)

        if (status.status === "complete") {
          clearInterval(pollRef.current)
          finishDelete()
        } else if (status.status === "failed") {
          clearInterval(pollRef.current)
          IsDeleting.setValue(false)
          setError(status.error || "Delete job failed. Please try again.")
          setStep("confirm")
          setInputValue("")
        }
      } catch (err) {
        // Polling endpoint not yet available — show queued message
        setProgress({ status: "queued" })
      }
    }, POLL_INTERVAL_MS)
  }

  const handleDelete = async () => {
    setStep("deleting")
    IsDeleting.setValue(true)
    setError(null)

    try {
      let result

      if (type === "records") {
        await api.recordsBulkDestroy(selectedIds)
        finishDelete()
        return
      }

      if (type === "schema") {
        result = await api.schemasDestroy(id)
      } else {
        result = await api.datasetsDestroy(id)
      }

      if (result?.job_id) {
        // Async path — backend is handling a large delete
        startPolling(result.job_id)
      } else {
        // Sync path — done immediately
        finishDelete()
      }
    } catch (err) {
      IsDeleting.setValue(false)
      let message = await err
        .json?.()
        .then((d) => d?.detail || d?.error)
        .catch(() => null)
      if (!message && type === "records" && err?.status === 504) {
        message =
          "Delete timed out at the proxy. The record may still be deleting in the background, especially if a larger delete was already running. Refresh and confirm before retrying."
      }
      setError(message || "Something went wrong. Please try again.")
      setStep("confirm")
      setInputValue("")
    }
  }

  const progressPercent =
    progress?.total > 0
      ? Math.round((progress.processed / progress.total) * 100)
      : null

  return (
    <Modal
      isOpen={isOpen}
      onClose={step === "deleting" ? undefined : onClose}
      closeOnOverlayClick={step !== "deleting"}
      closeOnEsc={step !== "deleting"}
      isCentered
      size="md"
    >
      <ModalOverlay />
      <ModalContent>
        {/* Step 1 — typed confirmation */}
        {step === "confirm" && (
          <>
            <ModalHeader>
              <HStack spacing={2}>
                <WarningTwoIcon color="red.400" />
                <Text>
                  Delete{" "}
                  {type === "records"
                    ? `${count} Record${count === 1 ? "" : "s"}`
                    : `${type === "dataset" ? "Dataset" : "Schema"} "${name}"`}
                </Text>
              </HStack>
            </ModalHeader>
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                {error && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <AlertDescription fontSize="sm">{error}</AlertDescription>
                  </Alert>
                )}
                <Text fontSize="sm" color="gray.600">
                  {descriptionText()}
                </Text>
                <VStack align="stretch" spacing={1}>
                  <Text fontSize="sm" fontWeight="medium">
                    {confirmPrompt()}
                  </Text>
                  <Input
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder={expectedPhrase}
                    size="sm"
                    autoFocus
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && confirmMatches)
                        handleConfirmNext()
                    }}
                  />
                </VStack>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <HStack spacing={3}>
                <Button variant="ghost" size="sm" onClick={onClose}>
                  Cancel
                </Button>
                <Button
                  colorScheme="red"
                  size="sm"
                  isDisabled={!confirmMatches}
                  onClick={handleConfirmNext}
                >
                  Delete
                </Button>
              </HStack>
            </ModalFooter>
          </>
        )}

        {/* Step 2 — final friction gate with countdown */}
        {step === "verify" && (
          <>
            <ModalHeader>
              <HStack spacing={2}>
                <WarningTwoIcon color="red.500" />
                <Text>Are you absolutely sure?</Text>
              </HStack>
            </ModalHeader>
            <ModalBody>
              <Text fontSize="sm" color="gray.600">
                You are about to permanently delete{" "}
                {type === "records"
                  ? `${count} record${count === 1 ? "" : "s"}`
                  : `"${name}" and all associated data`}
                . <strong>There is no undo.</strong>
              </Text>
            </ModalBody>
            <ModalFooter>
              <HStack spacing={3}>
                <Button variant="ghost" size="sm" onClick={handleBack}>
                  ← Go back
                </Button>
                <Button
                  colorScheme="red"
                  size="sm"
                  isDisabled={countdown > 0}
                  onClick={handleDelete}
                >
                  {countdown > 0 ? `Wait ${countdown}…` : "Yes, delete forever"}
                </Button>
              </HStack>
            </ModalFooter>
          </>
        )}

        {/* Step 3 — in-flight / progress */}
        {step === "deleting" && (
          <>
            <ModalHeader>
              <Text>Deleting…</Text>
            </ModalHeader>
            <ModalBody>
              <VStack align="stretch" spacing={4}>
                {progress?.status === "queued" ? (
                  <Text fontSize="sm" color="gray.600">
                    Delete queued — this may take a few minutes for large
                    datasets. You can dismiss this and check back.
                  </Text>
                ) : progressPercent !== null ? (
                  <>
                    <Text fontSize="sm" color="gray.600">
                      Deleting records… {progress.processed.toLocaleString()} /{" "}
                      {progress.total.toLocaleString()}
                    </Text>
                    <Progress
                      value={progressPercent}
                      size="sm"
                      colorScheme="red"
                      borderRadius="md"
                      hasStripe
                      isAnimated
                    />
                  </>
                ) : (
                  <Progress
                    size="sm"
                    colorScheme="red"
                    borderRadius="md"
                    isIndeterminate
                  />
                )}
              </VStack>
            </ModalBody>
            {progress?.status === "queued" && (
              <ModalFooter>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    clearInterval(pollRef.current)
                    IsDeleting.setValue(false)
                    onClose()
                  }}
                >
                  Got it, dismiss
                </Button>
              </ModalFooter>
            )}
          </>
        )}
      </ModalContent>
    </Modal>
  )
}
